import { brandsOpenApi, CUSTOM_CREATIVES_SIZES } from '@constants';
import { axiosBoa } from '@utils';
import { getParent, types } from 'mobx-state-tree';
import { CustomCreativeUnitModel } from './constants/customCreativeUnitModel';
import { createStoreAction } from './utils';

export const CustomCreativesStore = types
    .model('CustomCreativesStore', {
        id: types.maybeNull(types.number),
        enabledByBrand: types.maybeNull(types.boolean),
        customCreativeUrl: types.maybeNull(types.string),
        units: types.array(CustomCreativeUnitModel),
        unitPreviewImageSrc: types.maybeNull(types.string),
        dbEnabledByBrand: types.maybeNull(types.boolean),
        dbCustomCreativeUrl: types.maybeNull(types.string),
        pending: false
    })
    .views((self) => ({
        get root() {
            return getParent(self);
        },
        get unitsWithImages() {
            return self.units.filter((unit) => !!unit.localData.imageSrc || !!unit.localData.file);
        },
        get isRejected() {
            return self.units.some((unit) => unit.serverData.validationStatus === 0);
        },
        get customCreativePreviewSrc() {
            const unit = self.units.find((unit) => unit.size === '300x600');
            // Return File URL or S3 URL
            const fileUrl = unit?.localData?.file ? URL.createObjectURL(unit.localData.file) : null;
            return fileUrl || unit?.localData?.imageSrc || null;
        },
        get isDataModified() {
            const isToggleModified = self.enabledByBrand !== self.dbEnabledByBrand;
            const isUrlModified = self.customCreativeUrl !== self.dbCustomCreativeUrl;
            const isUnitsModified = self.units.some((unit) => unit.isModified());
            return isToggleModified || isUrlModified || isUnitsModified;
        }
    }))
    .actions((self) => {
        const action = createStoreAction(self);

        return {
            setEnabledByBrand(value) {
                self.enabledByBrand = value;
            },
            setCreativeUrl(value) {
                self.customCreativeUrl = value;
            },
            setUnitsFromServer(serverData) {
                const serverMap = new Map(serverData.map((item) => [item.size, item]));
                // Initialize all sizes with server data or defaults
                self.units = CUSTOM_CREATIVES_SIZES.map(({ size }) =>
                    CustomCreativeUnitModel.create({
                        size,
                        serverData: serverMap.get(size) || { validationStatus: 1, imageSrc: '' },
                        localData: serverMap.get(size) || { validationStatus: 1, imageSrc: '' }
                    })
                );
            },
            getUnitBySize(size) {
                return self.units.find((unit) => unit.size === size);
            },
            updateUnitField(size, field, value) {
                const unit = self.getUnitBySize(size);
                if (unit) {
                    unit.updateField(field, value);
                }
            },
            resetUnit(size) {
                const unit = self.getUnitBySize(size);
                if (unit) {
                    unit.resetToServerData();
                }
            },
            getCustomCreatives: action(
                function* getCustomCreatives(campaignId) {
                    self.pending = true;

                    const response = yield axiosBoa.get(brandsOpenApi.customCreatives.get(campaignId));
                    self.enabledByBrand = response.enabledByBrand;
                    self.customCreativeUrl = response.customCreativeUrl;
                    self.setUnitsFromServer(response.units);
                    self.dbEnabledByBrand = response.enabledByBrand;
                    self.dbCustomCreativeUrl = response.customCreativeUrl;
                },
                { onFinally: () => (self.pending = false) }
            ),
            prepareFormData() {
                const formData = new FormData();
                formData.append('enabledByBrand', self.enabledByBrand);
                formData.append('customCreativeUrl', self.customCreativeUrl);
                self.units.forEach((unit) => {
                    const size = unit.size;
                    // New or updated file: Add to FormData
                    if (unit.isModified() && unit.localData.file) {
                        const file = unit.localData.file;
                        const extFromName = file.name && file.name.match(/\.([^.]+)$/i) ? RegExp.$1 : '';
                        const mimeToExt = { 'image/jpeg': 'jpg', 'image/png': 'png' };
                        const extension = (extFromName || mimeToExt[file.type] || 'jpg').toLowerCase();
                        formData.append('files[]', file, `unit_${size}.${extension}`);
                        formData.append('updatedSizes[]', unit.size);
                    }
                    // Mark deleted files
                    if (unit.isDeleted()) {
                        formData.append('deletedSizes[]', unit.size);
                    }
                });
                return formData;
            },
            updateCustomCreatives: action(function* updateCustomCreatives(campaignId) {
                if (!campaignId || !self.isDataModified) return false;

                const formData = self.prepareFormData(campaignId);
                yield axiosBoa.post(brandsOpenApi.customCreatives.update(campaignId), formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            })
        };
    });
